import { useEffect, useState } from 'react';
import PagarPedidoContainer from '../../../../../../containers/pagarPedido/pagarPedidoContainer';
import * as S from './adicionarSaldoBoletoStyles';
import StateMensage from 'base/src/view/states/state';
import { OBTER_BOLETO, GERAR_BOLETO } from '../../../../../../containers/pagarPedido/pagarPedidoContainer';
import { CampanhaPedidoResgatePagamentoDepositoResponse } from '../../../../../../types/pagarPedido/campanhaPedidoResgatePagamentoDepositoResponse';
import { GerarBoletoResponse } from '../../../../../../types/lotePedido/gerarBoletoResponse';
import { Button } from 'base/src/components/Button';
import { formatToBRL } from 'base/src/utils/mathUtil';

export type AdicionarSaldoBoletoProps = {
  campanhaPedidoResgatePagamentoId: number;
  ValorTotal: string;
  loteId?: number;
  carteiraId: number;
  onClose?: () => void;
}

export function AdicionarSaldoBoleto(props: AdicionarSaldoBoletoProps) {
  const _pagarPedidoContainer = new PagarPedidoContainer();
  const [boleto, setBoleto] = useState<CampanhaPedidoResgatePagamentoDepositoResponse | null>(null);
  const [boletoError, setBoletoError] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [boletoNaoEncontrado, setBoletoNaoEncontrado] = useState<boolean>(false);
  const [gerandoBoleto, setGerandoBoleto] = useState<boolean>(false);
  const [boletoGerado, setBoletoGerado] = useState<GerarBoletoResponse | null>(null);

  function abrirBoleto() {
    if (boletoGerado?.PaymentUrl) {
      window.open(boletoGerado.PaymentUrl, '_blank');
    }
  }

  async function gerarBoleto() {
    try {
      setGerandoBoleto(true);
      const result = await _pagarPedidoContainer.gerarBoleto(props.campanhaPedidoResgatePagamentoId, props.carteiraId);

      if (result instanceof Error) {
        setBoletoError(true);
      } else {
        // Gerar boleto foi bem-sucedido
        setBoletoGerado(result);
        setBoletoError(false);
      }
    } catch (error) {
      setBoletoError(true);
    } finally {
      setGerandoBoleto(false);
    }
  }

  useEffect(function obterBoletoEffect() {
    async function obterBoleto() {
      try {
        setLoading(true);
        const result = await _pagarPedidoContainer.obterBoleto(props.campanhaPedidoResgatePagamentoId);

        if (result instanceof Error) {
          setBoletoError(true);
          setBoletoNaoEncontrado(false);
          setBoleto(null);
        } else {
          // Verifica se a resposta tem a nova estrutura da API
          const hasApiStructure = result && typeof result === 'object' && 'Success' in result;

          if (hasApiStructure) {
            const apiResponse = result as unknown as { Success: boolean; Message: string; Data: CampanhaPedidoResgatePagamentoDepositoResponse | null };

            if (!apiResponse.Success && apiResponse.Message === "Pagamento não encontrado") {
              setBoletoNaoEncontrado(true);
              setBoletoError(false);
              setBoleto(null);
            } else if (!apiResponse.Success) {
              setBoletoError(true);
              setBoletoNaoEncontrado(false);
              setBoleto(null);
            } else {
              setBoleto(apiResponse.Data);
              setBoletoError(false);
              setBoletoNaoEncontrado(false);
            }
          } else {
            // Resposta no formato antigo - trata como sucesso
            setBoleto(result as CampanhaPedidoResgatePagamentoDepositoResponse);
            setBoletoError(false);
            setBoletoNaoEncontrado(false);
          }
        }
      } catch (error: unknown) {
        setBoletoError(true);
        setBoletoNaoEncontrado(false);
        setBoleto(null);
      } finally {
        setLoading(false);
      }
    }

    obterBoleto();
  }, [props.campanhaPedidoResgatePagamentoId]);

  // Mostrar loading enquanto busca o boleto ou está gerando boleto
  if (loading || gerandoBoleto) {
    return (
      <S.Container>
        <StateMensage stateLoadingNames={[OBTER_BOLETO, GERAR_BOLETO]} />
      </S.Container>
    );
  }

  // Se encontrou o boleto (200), significa que já foi gerado - chama gerarBoleto automaticamente
  if (boleto && !boletoError && !boletoGerado) {
    // Chama gerarBoleto automaticamente para obter o link
    gerarBoleto();

    return (
      <S.Container>
        <StateMensage stateLoadingNames={[GERAR_BOLETO]} />
      </S.Container>
    );
  }

  // Se o boleto foi gerado (response do gerar boleto), mostrar preview e download
  if (boletoGerado && !boletoError) {
    return (
      <S.Container>

        <S.InfoSection>
          {/* Informações do Boleto */}
          <S.InfoBlock>
            <S.SectionTitle>🎫 Dados do Boleto</S.SectionTitle>
            <S.InfoGrid>
              <S.InfoCard>
                <S.InfoLabel>ID do Boleto</S.InfoLabel>
                <S.InfoValue>{boletoGerado.GeneratedId}</S.InfoValue>
              </S.InfoCard>

              <S.InfoCard>
                <S.InfoLabel>Transaction ID</S.InfoLabel>
                <S.InfoValue>{boletoGerado.TransactionId}</S.InfoValue>
              </S.InfoCard>


              {boletoGerado.Msg && (
                <S.InfoCard fullWidth>
                  <S.InfoLabel>Mensagem</S.InfoLabel>
                  <S.InfoValue>{boletoGerado.Msg}</S.InfoValue>
                </S.InfoCard>
              )}


              <S.InfoCard>
                <S.InfoLabel>Status</S.InfoLabel>
                <S.InfoValue color={boletoGerado.Success ? 'success' : 'danger'}>
                  {boletoGerado.Success ? 'Sucesso' : 'Erro'}
                </S.InfoValue>
              </S.InfoCard>
            </S.InfoGrid>
          </S.InfoBlock>

          {/* Informações do Depósito */}
          {boleto && (
            <S.InfoBlock>
              <S.SectionTitle>📋 Dados do Depósito</S.SectionTitle>
              <S.InfoGrid>
                <S.InfoCard>
                  <S.InfoLabel>ID Depósito</S.InfoLabel>
                  <S.InfoValue>#{boleto.CampanhaPedidoResgatePagamentoDepositoID}</S.InfoValue>
                </S.InfoCard>

                <S.InfoCard>
                  <S.InfoLabel>Status</S.InfoLabel>
                  <S.InfoValue>{boleto.Status}</S.InfoValue>
                </S.InfoCard>

                {boleto.ValorDeposito && (
                  <S.InfoCard>
                    <S.InfoLabel>Valor do Depósito</S.InfoLabel>
                    <S.InfoValue color="success" size="lg">
                      R$ {Number(boleto.ValorDeposito).toFixed(2)}
                    </S.InfoValue>
                  </S.InfoCard>
                )}

                {boleto.ValorTaxa && (
                  <S.InfoCard>
                    <S.InfoLabel>Taxa</S.InfoLabel>
                    <S.InfoValue color="warning">
                      R$ {Number(boleto.ValorTaxa).toFixed(2)}
                    </S.InfoValue>
                  </S.InfoCard>
                )}

                <S.InfoCard>
                  <S.InfoLabel>Usuário</S.InfoLabel>
                  <S.InfoValue>{boleto.Usuario}</S.InfoValue>
                </S.InfoCard>

                <S.InfoCard>
                  <S.InfoLabel>Data de Criação</S.InfoLabel>
                  <S.InfoValue>{new Date(boleto.CriadoEm).toLocaleDateString('pt-BR')}</S.InfoValue>
                </S.InfoCard>
              </S.InfoGrid>
            </S.InfoBlock>
          )}
        </S.InfoSection>

        {/* Botão de abrir boleto modernizado */}
        {boletoGerado.PaymentUrl && (
          <S.ComprovanteContainer>
            <Button size='sm' variant='success' full onClick={abrirBoleto}>
              Abrir Boleto
            </Button>
          </S.ComprovanteContainer>
        )}
      </S.Container>
    );
  }

  // Se não encontrou o boleto (404), mostrar opção para gerar boleto
  if (boletoNaoEncontrado) {
    return (
      <S.Container>

        <S.AvisoContainer>
          <S.AvisoTitle>
            📄 Boleto não encontrado
          </S.AvisoTitle>
          <S.AvisoText>
            Não existe um boleto gerado para este depósito. Clique no botão abaixo para gerar um novo boleto.
          </S.AvisoText>
        </S.AvisoContainer>

        <S.InfoSection>
          <S.InfoBlock>
            <S.SectionTitle>📋 Informações do Pedido</S.SectionTitle>
            <S.InfoGrid>
              <S.InfoCard>
                <S.InfoLabel>ID do Pagamento</S.InfoLabel>
                <S.InfoValue>{props.campanhaPedidoResgatePagamentoId}</S.InfoValue>
              </S.InfoCard>

              <S.InfoCard>
                <S.InfoLabel>Valor Total</S.InfoLabel>
                <S.InfoValue color="success" size="lg">{formatToBRL(props.ValorTotal)}</S.InfoValue>
              </S.InfoCard>

              <S.InfoCard>
                <S.InfoLabel>Lote ID</S.InfoLabel>
                <S.InfoValue>{props.loteId}</S.InfoValue>
              </S.InfoCard>

              <S.InfoCard>
                <S.InfoLabel>Carteira ID</S.InfoLabel>
                <S.InfoValue>{props.carteiraId}</S.InfoValue>
              </S.InfoCard>
            </S.InfoGrid>
          </S.InfoBlock>
        </S.InfoSection>

        <S.ComprovanteContainer>
          <Button size='sm' variant='success' full onClick={gerarBoleto}>
            {gerandoBoleto ? '⏳ Gerando...' : 'Gerar Boleto'}
          </Button>
        </S.ComprovanteContainer>
      </S.Container>
    );
  }

  // Função para tentar novamente
  async function tentarNovamente() {
    setBoletoError(false);
    setBoletoNaoEncontrado(false);
    setBoleto(null);
    setBoletoGerado(null);
    setLoading(true);

    try {
      const result = await _pagarPedidoContainer.obterBoleto(props.campanhaPedidoResgatePagamentoId);

      if (result instanceof Error) {
        setBoletoError(true);
      } else {
        // Verifica se a resposta tem a nova estrutura da API
        const hasApiStructure = result && typeof result === 'object' && 'Success' in result;

        if (hasApiStructure) {
          const apiResponse = result as unknown as { Success: boolean; Message: string; Data: CampanhaPedidoResgatePagamentoDepositoResponse | null };

          if (!apiResponse.Success && apiResponse.Message === "Pagamento não encontrado") {
            setBoletoNaoEncontrado(true);
            setBoletoError(false);
          } else if (!apiResponse.Success) {
            setBoletoError(true);
          } else {
            setBoleto(apiResponse.Data);
            setBoletoError(false);
          }
        } else {
          // Resposta no formato antigo - trata como sucesso
          setBoleto(result as CampanhaPedidoResgatePagamentoDepositoResponse);
          setBoletoError(false);
        }
      }
    } catch (error: unknown) {
      setBoletoError(true);
    } finally {
      setLoading(false);
    }
  }

  // Se houve erro (não 404), mostrar mensagem de erro
  if (boletoError) {
    return (
      <S.Container>

        <S.AvisoContainer>
          <S.AvisoTitle>
            ❌ Erro encontrado
          </S.AvisoTitle>
          <S.AvisoText>
            Ocorreu um erro ao tentar obter o boleto. Tente novamente ou entre em contato com o suporte.
          </S.AvisoText>
        </S.AvisoContainer>
        <S.ComprovanteContainer>
          <Button size='sm' variant='success' full onClick={tentarNovamente}>
            Tentar Novamente
          </Button>
        </S.ComprovanteContainer>
      </S.Container>
    );
  }

  // Se não encontrou o boleto ou houve erro, mostrar o componente PagamentoBoleto
  return (
    <S.Container>
      <div>

        <S.AvisoContainer>
          <S.AvisoTitle>
            ⚠️ Estado não reconhecido
          </S.AvisoTitle>
          <S.AvisoText>
            Não foi possível determinar o estado atual do boleto. Tente recarregar a página.
          </S.AvisoText>
        </S.AvisoContainer>
      </div>
    </S.Container>
  );
}

export default AdicionarSaldoBoleto;